import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApiError,
  api,
  awaitingDecision,
  can,
  daysUntil,
  readByWords,
  refusalReason,
  shortDate,
  type DocumentDetail,
  type Issue,
  type Proposal,
  type ProposalSet,
  type Questionnaire,
  type RiskModel,
  type VendorDocument,
} from '../lib/api'
import { statusWord } from '../lib/status'
import { Chip } from './StatusChip'
import { AGREEMENT_KINDS, CONTRACT_KINDS, DOC_TYPES, docTypeWords } from '../lib/documentTypes'
import { checkCertificateIssued, checkDocumentDates, filingChecks, todayIndia, useLeftFields } from '../lib/dateRules'
import { DateRuleNote, DateWarnNote } from './DateRuleNote'
import { AutoRenewBox, AutoRenewField } from './AutoRenew'
import { DataTable, ErrorBox, Loading, type Column } from './DataTable'
import { Dialog, Status, Toggletip, type Tone } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import '../styles/sweep.css'

// These are the eleven values vendor_documents accepts, and they have to stay
// the eleven: documents.go holds the same list and migration 0002 holds the
// CHECK behind it.
//
// This list used to disagree with both. It offered SLA, POLICY, CERTIFICATE and
// AUDIT_REPORT, none of which the API accepts, so choosing any of them failed
// validation. Worse, it hid ISO27001, ISO27701, SOC2, PCI_DSS and CERTIN_AUDIT,
// which are exactly the five doc types the reassessment scheduler watches for
// expiry. A certificate of a watched type could not be uploaded through the
// console at all, so certificate expiry could never fire for anybody.

// The five the scheduler raises CERT_EXPIRY for. Used only to tell the reader
// that a date matters for this one, which is the difference between a
// certificate that produces a reminder and one that quietly lapses.
const EXPIRY_WATCHED = new Set(['ISO27001', 'ISO27701', 'SOC2', 'PCI_DSS', 'CERTIN_AUDIT'])


// Three verdicts rather than two. EDIT accepts the clause but corrects the
// value, which the API records as INTERNAL_ASSERTED instead of
// DOCUMENT_EXTRACTED, and that is why the note is mandatory on that path only.
type Verdict = 'ACCEPT' | 'EDIT' | 'REJECT'

type Draft = { verdict: Verdict; value: string; note: string }

function decidedStatus(status: string): 'accepted' | 'rejected' | null {
  const s = status.toUpperCase()
  if (s === 'ACCEPT' || s === 'ACCEPTED') return 'accepted'
  if (s === 'REJECT' || s === 'REJECTED') return 'rejected'
  return null
}

// Confidence is deliberately not shown as a percentage. A bare 62% reads as an
// accuracy claim, and nothing in this path compares the number to a threshold.
function confidenceReading(v: number): string {
  if (v >= 0.85) return 'the clause uses the words the rule looks for'
  if (v >= 0.65) return 'the phrase matched, so check the scope is the one the question asks about'
  return 'a loose match, so read the clause closely before agreeing'
}

function issuesByKey(err: unknown): Record<string, Issue> {
  const out: Record<string, Issue> = {}
  if (err instanceof ApiError) for (const i of err.issues) out[i.field] = i
  return out
}

function plural(n: number, one: string, many: string): string {
  return n + ' ' + (n === 1 ? one : many)
}

// What the worker made of a file (release 3.3). The file is read off the
// upload, masked before any of its text is kept, and a file that could not be
// read says why: "not read" is about us, never about what the contract says.
function readingStatus(state?: string, note?: string): { tone: Tone; word: string; detail: string } {
  switch (state) {
    case 'QUEUED':
    case 'READING':
      return { tone: 'info', word: 'Being read', detail: note || 'Ready in a moment' }
    case 'NOT_READ':
      return { tone: 'crit', word: 'Not read', detail: note || 'It could not be read' }
    case 'READ':
      return note ? { tone: 'warn', word: 'Read', detail: note } : { tone: 'ok', word: 'Read', detail: '' }
  }
  return { tone: 'neutral', word: 'Not read yet', detail: '' }
}

function stillReading(state?: string): boolean {
  return state === 'QUEUED' || state === 'READING'
}

// The build's refusals a person can act on arrive as validation issues: no
// proposal accepted anywhere, or no contact who could confirm the result. The
// error's own message is the path and a status code; the sentence worth
// showing is the issue's detail, and the next step depends on which it was.
type BuildRefusal = { code: 'NOTHING_ACCEPTED' | 'NOBODY_TO_CONFIRM'; detail: string }

function buildRefusal(err: unknown): BuildRefusal | null {
  if (!(err instanceof ApiError)) return null
  for (const code of ['NOTHING_ACCEPTED', 'NOBODY_TO_CONFIRM'] as const) {
    const issue = err.issues.find((i) => i.code === code)
    if (issue) return { code, detail: issue.detail }
  }
  return null
}

function fields(raw: unknown): Record<string, unknown> {
  return raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
}

function text(raw: unknown): string {
  return typeof raw === 'string' ? raw : ''
}

// The upload answers with the reason extraction is unavailable under its own
// key, not in `note`. Reading `note` instead repeats "the document is on file"
// and loses the only sentence that says why nothing was read.
function unavailableReason(raw: unknown): string {
  return text(fields(raw).extractionUnavailable)
}

// A conflict is another clause of the same document that answered the same
// question differently. The matcher keeps the stronger one; showing only that
// one turns a contract that says two things into a settled reading.
type Conflict = { locator: string; proposedValue: string; snippet: string }

function conflictsOf(raw: unknown): Conflict[] {
  const list = fields(raw).conflicts
  if (!Array.isArray(list)) return []
  return list.flatMap((c) => {
    const o = fields(c)
    const locator = text(o.locator)
    const proposedValue = text(o.proposedValue)
    if (locator === '' || proposedValue === '') return []
    return [{ locator, proposedValue, snippet: text(o.snippet) }]
  })
}

// What the run did not propose. Only the extract response carries it, so it
// describes the run just performed and is cleared when another document opens.
type RunCoverage = { unanswered: string[]; unusable: { questionKey: string; reason: string }[] }

function coverageOf(raw: unknown): RunCoverage {
  const o = fields(raw)
  const unanswered = Array.isArray(o.unanswered)
    ? o.unanswered.flatMap((k) => (typeof k === 'string' ? [k] : []))
    : []
  const unusable = Array.isArray(o.unusable)
    ? o.unusable.flatMap((u) => {
        const r = fields(u)
        const questionKey = text(r.questionKey)
        const reason = text(r.reason)
        return questionKey === '' ? [] : [{ questionKey, reason }]
      })
    : []
  return { unanswered, unusable }
}

// contractDates are the vendor record's contract start and end (asked once, at
// intake). A contract filed here offers them as its own dates, marked with
// where they came from, rather than asking for them again (release 3.2,
// removal item 15).
export function VendorContracts({ vendorId, contractDates }: { vendorId: string; contractDates?: { start?: string; end?: string } }) {
  const [docs, setDocs] = useState<VendorDocument[] | null>(null)
  const [docsError, setDocsError] = useState<unknown>(null)

  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([])
  const [models, setModels] = useState<RiskModel[]>([])

  const [file, setFile] = useState<File | null>(null)
  const [docType, setDocType] = useState('MSA')
  const [title, setTitle] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState(() => (contractDates?.start ?? '').slice(0, 10))
  const [expiresOn, setExpiresOn] = useState(() => (contractDates?.end ?? '').slice(0, 10))
  // Whether the dates on the form are still the record's, so the form can say
  // where they came from until somebody changes them.
  const fromRecord =
    CONTRACT_KINDS.has(docType) &&
    ((effectiveFrom !== '' && effectiveFrom === (contractDates?.start ?? '').slice(0, 10)) ||
      (expiresOn !== '' && expiresOn === (contractDates?.end ?? '').slice(0, 10)))
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<unknown>(null)
  const [uploadNote, setUploadNote] = useState('')

  // Deciding what a supplier sent. decliningId opens the reason box for one
  // row: a refusal with nothing said reads to the supplier exactly like being
  // ignored, and the API refuses one without.
  const [deciding, setDeciding] = useState<string | null>(null)
  const [decliningId, setDecliningId] = useState<string | null>(null)
  const [declineReason, setDeclineReason] = useState('')
  const [decideError, setDecideError] = useState<unknown>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [detail, setDetail] = useState<DocumentDetail | null>(null)
  const [detailError, setDetailError] = useState<unknown>(null)
  const [set, setSet] = useState<ProposalSet | null>(null)

  const [qCode, setQCode] = useState('')
  const [extracting, setExtracting] = useState(false)
  const [extractError, setExtractError] = useState<unknown>(null)
  const [coverage, setCoverage] = useState<RunCoverage | null>(null)

  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [partial, setPartial] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<unknown>(null)
  const [submitNote, setSubmitNote] = useState('')

  const [buildQ, setBuildQ] = useState('')
  const [buildModel, setBuildModel] = useState('')
  const [buildDays, setBuildDays] = useState('90')
  // A suspended vendor may be sent due diligence with a reason (C18). The box
  // appears once the API has asked for one, so nobody types a reason the
  // vendor's status does not need (review of 12 Oct, S5).
  const [buildReason, setBuildReason] = useState('')
  const [building, setBuilding] = useState(false)
  const [buildError, setBuildError] = useState<unknown>(null)
  const [built, setBuilt] = useState<{ reference: string; due: string } | null>(null)
  const [aboutBuild, setAboutBuild] = useState(false)

  const published = useMemo(() => questionnaires.filter((q) => q.publishedVersionId), [questionnaires])

  const loadDocs = useCallback(() => {
    api
      .vendorDocuments(vendorId)
      .then((r) => {
        setDocs(r.documents)
        setDocsError(null)
      })
      .catch(setDocsError)
  }, [vendorId])

  useEffect(loadDocs, [loadDocs])

  // A file is read by the worker a few seconds after it is uploaded. While any
  // document here is still being read, the list and the open document are
  // asked again every three seconds, and the asking stops when none is.
  const reading = (docs ?? []).some((d) => stillReading(d.readState)) || stillReading(detail?.reading?.state)
  useEffect(() => {
    if (!reading) return
    const timer = window.setTimeout(() => {
      loadDocs()
      if (openId !== null) {
        api
          .document(openId)
          .then(setDetail)
          .catch(() => {})
      }
    }, 3000)
    return () => window.clearTimeout(timer)
  }, [reading, docs, detail, openId, loadDocs])

  const [rereading, setRereading] = useState(false)
  const [rereadError, setRereadError] = useState<unknown>(null)
  function readAgain(id: string) {
    setRereading(true)
    setRereadError(null)
    api
      .readDocumentAgain(id)
      .then(() => api.document(id))
      .then((d) => {
        setDetail(d)
        loadDocs()
      })
      .catch(setRereadError)
      .finally(() => setRereading(false))
  }

  useEffect(() => {
    // Reading a filed contract into answers needs the questionnaires and the
    // models; a reader who may not review evidence or read the library is
    // offered no reading, so it asks for neither.
    if (!can('config.read') || !can('evidence.review')) {
      setQuestionnaires([])
      setModels([])
      return
    }
    api
      .questionnaires()
      .then((r) => setQuestionnaires(r.questionnaires))
      .catch(() => setQuestionnaires([]))
    api
      .riskModels()
      .then((r) => setModels(r.riskModels.filter((m) => m.activeVersionId)))
      .catch(() => setModels([]))
  }, [])

  useEffect(() => {
    const first = published[0]
    if (!first) return
    setQCode((c) => c || first.code)
    setBuildQ((c) => c || first.code)
  }, [published])

  const loadProposals = useCallback((documentId: string) => {
    return api
      .proposals(documentId)
      .then((s) => {
        setSet(s)
        setDrafts({})
        return s
      })
      .catch((err) => {
        // A document nobody has read yet is a normal state, not a failure.
        // Anything else still deserves the error box.
        if (err instanceof ApiError && (err.code === 'NOT_EXTRACTED' || err.status === 404)) {
          setSet(null)
          return null
        }
        throw err
      })
  }, [])

  function open(documentId: string) {
    setOpenId(documentId)
    setDetail(null)
    setSet(null)
    setDrafts({})
    setDetailError(null)
    setExtractError(null)
    setSubmitError(null)
    setSubmitNote('')
    setPartial(false)
    setCoverage(null)
    api
      .document(documentId)
      .then((d) => {
        setDetail(d)
        // Preselect the questionnaire the last run used, so re-reading does not
        // quietly switch which questions the contract is being read against.
        const match = questionnaires.find((q) => q.publishedVersionId === d.extraction?.questionnaireVersionId)
        if (match) setQCode(match.code)
        return loadProposals(documentId)
      })
      .catch(setDetailError)
  }

  // D6 and D7 of the date rules, said on the field once the person leaves it.
  const leftDates = useLeftFields()
  const issuedRule = AGREEMENT_KINDS.has(docType) ? '' : checkCertificateIssued(effectiveFrom, todayIndia())
  // D8, D9, D16 and D17 (release 3.3, M4): a renewal must end after the
  // contract it renews; the rest are amber and never block.
  const filing = filingChecks(docType, effectiveFrom, expiresOn, docs ?? [], !AGREEMENT_KINDS.has(docType) && docType !== 'OTHER', todayIndia())
  const expiryRule = checkDocumentDates(effectiveFrom, expiresOn) || filing.expiryError
  const [autoRenew, setAutoRenew] = useState(false)

  function upload() {
    if (!file) return
    if (issuedRule || expiryRule) {
      leftDates.leave('effectiveFrom', 'expiresOn')
      return
    }
    setUploading(true)
    setUploadError(null)
    setUploadNote('')
    api
      .uploadVendorDocument(vendorId, file, docType, title.trim(), {
        effectiveFrom: effectiveFrom || undefined,
        expiresOn: expiresOn || undefined,
        autoRenew: CONTRACT_KINDS.has(docType) && autoRenew,
      })
      .then((r) => {
        // Release 3.3: the file is read after the upload, by the worker, and
        // personal data values in it are masked before any text is kept. The
        // list below shows when it has been read.
        const why = unavailableReason(r)
        setUploadNote(
          why
            ? 'On file, but no clause can be read from it: ' + why
            : 'On file and being read. Personal data values in it are masked before any of its text is kept.',
        )
        setFile(null)
        setTitle('')
        loadDocs()
      })
      .catch(setUploadError)
      .finally(() => setUploading(false))
  }

  function extract() {
    const id = openId
    if (!id || qCode === '') return
    setExtracting(true)
    setExtractError(null)
    setSubmitNote('')
    setCoverage(null)
    api
      .extractDocument(id, qCode)
      // The extract response and the proposals read are assembled by two
      // different handlers. Reading the list back means the screen shows the
      // same rows the provisional build will later use. The extract response is
      // still the only place the questions that drew nothing are reported, so
      // it is read for those before the list replaces it.
      .then((run) => {
        setCoverage(coverageOf(run))
        return loadProposals(id)
      })
      .then(() => api.document(id))
      .then((d) => {
        setDetail(d)
        // A new run leaves the old run's acceptances behind, so the count the
        // build is gated on has just changed for this document.
        loadDocs()
      })
      .catch(setExtractError)
      .finally(() => setExtracting(false))
  }

  function setVerdict(key: string, verdict: Verdict) {
    setDrafts((prev) => {
      const next = { ...prev }
      const existing = prev[key]
      if (existing && existing.verdict === verdict) {
        delete next[key]
        return next
      }
      next[key] = { verdict, value: existing?.value ?? '', note: existing?.note ?? '' }
      return next
    })
  }

  function patch(key: string, field: 'value' | 'note', v: string) {
    setDrafts((prev) => {
      const existing = prev[key]
      if (!existing) return prev
      return { ...prev, [key]: { ...existing, [field]: v } }
    })
  }

  const proposals: Proposal[] = set?.proposals ?? []
  const decided = proposals.filter((p) => drafts[p.questionKey])
  const undecided = proposals.filter((p) => !drafts[p.questionKey] && decidedStatus(p.status) === null)
  const acceptedOnDoc = proposals.filter((p) => decidedStatus(p.status) === 'accepted').length
  const rejectedOnDoc = proposals.filter((p) => decidedStatus(p.status) === 'rejected').length

  // Vendor-wide, because the build takes every document and not only the open
  // one. The list carries a count per document that the API reads through the
  // same fold the build reads, so a zero here is the zero the build refuses on.
  const onFile = docs ?? []
  const acceptedAcross = onFile.reduce((n, d) => n + (d.acceptedCount ?? 0), 0)
  const docsWithAccepted = onFile.filter((d) => (d.acceptedCount ?? 0) > 0).length
  const nothingAccepted = docs !== null && acceptedAcross === 0
  const refusal = buildRefusal(buildError)

  const incomplete = decided.filter((p) => {
    const d = drafts[p.questionKey]
    if (!d) return false
    return d.verdict === 'EDIT' && (d.value.trim() === '' || d.note.trim() === '')
  })

  const serverIssues = issuesByKey(submitError)

  function submit() {
    const id = openId
    if (!id) return
    const payload = decided.flatMap((p) => {
      const d = drafts[p.questionKey]
      if (!d) return []
      if (d.verdict === 'REJECT') {
        return [{ questionKey: p.questionKey, decision: 'REJECT', note: d.note.trim() }]
      }
      if (d.verdict === 'EDIT') {
        return [
          { questionKey: p.questionKey, decision: 'ACCEPT', value: d.value.trim(), note: d.note.trim() },
        ]
      }
      return [{ questionKey: p.questionKey, decision: 'ACCEPT' }]
    })
    if (payload.length === 0) return
    setSubmitting(true)
    setSubmitError(null)
    setSubmitNote('')
    api
      .confirmProposals(id, payload)
      .then(() => loadProposals(id))
      .then(() => {
        setSubmitNote(
          plural(payload.length, 'decision', 'decisions') +
            ' recorded, no assessment exists yet and nothing has scored',
        )
        setPartial(false)
        // The build's gate reads the list, and a refusal for nothing accepted
        // no longer describes this vendor once a decision has landed.
        loadDocs()
        setBuildError((e: unknown) => (buildRefusal(e)?.code === 'NOTHING_ACCEPTED' ? null : e))
      })
      .catch(setSubmitError)
      .finally(() => setSubmitting(false))
  }

  function build() {
    setBuilding(true)
    setBuildError(null)
    setBuilt(null)
    api
      .provisionalAssessment(vendorId, {
        questionnaireCode: buildQ,
        riskModelCode: buildModel,
        confirmationDueDays: Number(buildDays) || 90,
        ...(buildReason.trim() !== '' ? { reason: buildReason.trim() } : {}),
      })
      .then((r) => {
        setBuilt({
          reference: typeof r.reference === 'string' ? r.reference : 'created',
          due: typeof r.vendorConfirmationDue === 'string' ? r.vendorConfirmationDue : '',
        })
      })
      .catch((err) => {
        setBuildError(err)
        // The API found nothing accepted where this page showed some, so the
        // list is stale. Re-reading it puts the gate back in step with the API.
        if (buildRefusal(err)?.code === 'NOTHING_ACCEPTED') loadDocs()
      })
      .finally(() => setBuilding(false))
  }

  function decide(d: VendorDocument, accept: boolean, reason: string) {
    setDeciding(d.id)
    setDecideError(null)
    api
      .decideDocument(d.id, accept, reason)
      .then(() => {
        setDecliningId(null)
        setDeclineReason('')
        // Re-read rather than patch the row: accepting one can change what the
        // activation gate says about this vendor, and a list assembled from a
        // stale read plus one changed row is how those two disagree.
        loadDocs()
      })
      .catch(setDecideError)
      .finally(() => setDeciding(null))
  }

  const columns: Column<VendorDocument>[] = [
    {
      key: 'title',
      header: 'Document',
      sortValue: (d) => d.title,
      render: (d) => (
        <button type="button" className="linkish" onClick={() => open(d.id)}>
          {d.title}
        </button>
      ),
    },
    {
      key: 'docType',
      header: 'Type',
      sortValue: (d) => d.docType,
      render: (d) => (
        <>
          <span>{docTypeWords(d.docType)}</span>
          {CONTRACT_KINDS.has(d.docType) && <AutoRenewBox doc={d} onSaved={loadDocs} />}
        </>
      ),
    },
    {
      key: 'createdAt',
      header: 'Uploaded',
      sortValue: (d) => d.createdAt,
      render: (d) => (
        <>
          {shortDate(d.createdAt)}
          <span className="cell-sub">{d.uploadedBy}</span>
        </>
      ),
    },
    {
      // Whether the file has been read (release 3.3). A file not read says
      // why, so "no clause found" is never mistaken for "the contract is
      // silent".
      key: 'readState',
      header: 'Read',
      sortValue: (d) => d.readState ?? '',
      render: (d) => {
        const r = readingStatus(d.readState, d.readNote)
        return (
          <>
            <Status tone={r.tone}>{r.word}</Status>
            {r.detail !== '' && <span className="cell-sub">{r.detail}</span>}
          </>
        )
      },
    },
    {
      // Where it came from, and what became of it. Without this column the one
      // screen listing a vendor's documents shows a claim the supplier sent
      // and a contract the legal team filed as the same thing.
      key: 'source',
      header: 'Provenance',
      sortValue: (d) => (d.source === 'VENDOR' ? (d.acceptedAt ? '2' : d.declinedAt ? '3' : '0') : '1'),
      render: (d) => {
        if (d.source !== 'VENDOR') return <span className="cell-sub">Filed here</span>
        if (d.acceptedAt) {
          return (
            <>
              <Chip s={statusWord('document', 'VALID')} />
              <span className="cell-sub">{d.acceptedBy}</span>
            </>
          )
        }
        if (d.declinedAt) {
          return (
            <>
              <Chip s={statusWord('document', 'DECLINED')} />
              <span className="cell-sub">{d.declineReason}</span>
            </>
          )
        }
        return (
          <>
            <Chip s={statusWord('document', 'AWAITING')} />
            <span className="cell-sub">From the supplier, counts for nothing until accepted</span>
          </>
        )
      },
    },
    {
      key: 'decide',
      header: 'Decide',
      render: (d) =>
        awaitingDecision(d) ? (
          <div className="chiprow">
            <button
              type="button"
              className="primary"
              disabled={deciding === d.id || !can('evidence.review')}
              title={can('evidence.review') ? undefined : refusalReason('evidence.review')}
              onClick={() => decide(d, true, '')}
              data-action="tprm.vendor,vendor-record.a16"
            >
              Accept
            </button>
            <button
              type="button"
              disabled={deciding === d.id || !can('evidence.review')}
              title={can('evidence.review') ? undefined : refusalReason('evidence.review')}
              onClick={() => setDecliningId(decliningId === d.id ? null : d.id)}
            >
              Decline
            </button>
          </div>
        ) : (
          <span className="cell-sub">Nothing to decide</span>
        ),
    },
    {
      key: 'clauses',
      header: 'Clauses',
      render: (d) => (
        <button
          type="button"
          onClick={() => open(d.id)}
          aria-expanded={openId === d.id}
          aria-controls="vc-reading"
        >
          {openId === d.id ? 'Already open below' : 'Read clauses'}
        </button>
      ),
    },
  ]

  const dueDays = Number(buildDays) || 90

  return (
    <section className="panel">
      <div className="section-head">
        Contracts and legacy onboarding <span className="mono">{docs ? docs.length : 0} on file</span>
      </div>

      <div>

        {/* The one form that files a document on this vendor, and it always
            asks the dates (release 3.2, removal item 14): the overview's card
            that filed one without an expiry let certificates lapse silently. */}
        <h3>Add a document</h3>
        {uploadError != null && <ErrorBox error={uploadError} what="The file" verb="uploaded" />}

        <div className="form-grid">
          <div className="field">
            <label htmlFor="vc-file">File</label>
            <input
              id="vc-file"
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              aria-describedby="vc-file-note"
            />
          </div>

          <div className="field">
            <label htmlFor="vc-doctype">Document type</label>
            <select
              id="vc-doctype"
              value={docType}
              onChange={(e) => {
                const next = e.target.value
                setDocType(next)
                // A certificate has its own dates; the contract's are offered
                // only for a contract.
                if (!CONTRACT_KINDS.has(next) && fromRecord) {
                  setEffectiveFrom('')
                  setExpiresOn('')
                }
                if (CONTRACT_KINDS.has(next) && effectiveFrom === '' && expiresOn === '') {
                  setEffectiveFrom((contractDates?.start ?? '').slice(0, 10))
                  setExpiresOn((contractDates?.end ?? '').slice(0, 10))
                }
              }}
            >
              {DOC_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="vc-title">Title</label>
            <input
              id="vc-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              aria-describedby="vc-title-note"
            />
          </div>

          <div className="field">
            <label htmlFor="vc-effective">In force from</label>
            <input
              id="vc-effective"
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              onBlur={() => (expiresOn ? leftDates.leave('effectiveFrom', 'expiresOn') : leftDates.leave('effectiveFrom'))}
              aria-invalid={leftDates.shown('effectiveFrom', issuedRule) ? true : undefined}
              aria-describedby={leftDates.shown('effectiveFrom', issuedRule) ? 'vc-effective-rule' : undefined}
            />
            <DateRuleNote id="vc-effective-rule" message={leftDates.shown('effectiveFrom', issuedRule)} />
            <DateWarnNote id="vc-effective-warn" message={leftDates.shown('effectiveFrom', issuedRule ? '' : filing.fromWarn)} />
          </div>

          <div className="field">
            <label htmlFor="vc-expires">Expires on</label>
            <input
              id="vc-expires"
              type="date"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              onBlur={() => leftDates.leave('expiresOn')}
              aria-invalid={leftDates.shown('expiresOn', expiryRule) ? true : undefined}
              aria-describedby={leftDates.shown('expiresOn', expiryRule) ? 'vc-expires-rule vc-expires-note' : 'vc-expires-note'}
            />
            <DateRuleNote id="vc-expires-rule" message={leftDates.shown('expiresOn', expiryRule)} />
            <DateWarnNote id="vc-expires-warn" message={leftDates.shown('expiresOn', expiryRule ? '' : filing.expiryWarn)} />
            <p className="field-note" id="vc-expires-note">
              {fromRecord ? 'From the vendor record, change them if this document says otherwise. ' : ''}
              {EXPIRY_WATCHED.has(docType)
                ? 'Reminders come from this date only, without it the certificate lapses silently'
                : docType === 'MSA' || docType === 'SOW'
                  ? 'Renewal is raised from this date'
                  : 'Optional, reminders are raised from this date'}
            </p>
          </div>
          {CONTRACT_KINDS.has(docType) && <AutoRenewField checked={autoRenew} onChange={setAutoRenew} />}
        </div>

        <div className="chiprow">
          <button
            type="button"
            className="primary"
            onClick={upload}
            disabled={!file || uploading || !can('evidence.upload')}
            title={can('evidence.upload') ? undefined : refusalReason('evidence.upload')}
            data-action="tprm.vendor,vendor-record.a12"
          >
            {uploading ? 'Uploading' : 'Upload'}
          </button>
          {!can('evidence.upload') && (
            <span className="muted sw-row">
              <Status tone="neutral">Read only</Status>
              <ReasonTip label="Why you cannot upload" text={refusalReason('evidence.upload')} />
            </span>
          )}
          {uploadNote !== '' && (
            <span className="muted" role="status">
              {uploadNote}
            </span>
          )}
        </div>
      </div>

      {decideError != null && <ErrorBox error={decideError} what="That decision" verb="recorded" />}
      {decliningId && (
        <form
          className="reject-form"
          onSubmit={(e) => {
            e.preventDefault()
            const d = docs?.find((x) => x.id === decliningId)
            if (!d || !declineReason.trim()) return
            decide(d, false, declineReason.trim())
          }}
        >
          <label htmlFor="vc-decline-reason">Why are you declining it?</label>
          <div className="reject-row">
            <input
              id="vc-decline-reason"
              type="text"
              value={declineReason}
              aria-describedby="vc-decline-help"
              onChange={(e) => setDeclineReason(e.target.value)}
            />
            <button type="submit" disabled={!declineReason.trim()} data-action="tprm.vendor,vendor-record.a17">
              Decline it
            </button>
            <button type="button" onClick={() => setDecliningId(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {docsError != null && <ErrorBox error={docsError} what="This vendor's documents" />}
      {docs === null && docsError == null && <Loading what="the documents" />}
      {docs !== null && (
        <DataTable
          caption="Contracts and other documents on file for this vendor"
          columns={columns}
          rows={docs}
          rowKey={(d) => d.id}
          defaultSort={{ key: 'createdAt', dir: 'desc' }}
          empty={{
            title: 'No contract on file',
            detail: 'Upload the signed agreement to read its clauses into proposed answers',
          }}
        />
      )}

      {openId !== null && (
        <div id="vc-reading">
          <div className="section-head">
            Reading the clauses <span className="mono">{detail?.document.title ?? openId}</span>
          </div>

          <div>
            {detailError != null && <ErrorBox error={detailError} what="This document" />}
            {detail === null && detailError == null && <Loading what="the document" />}

            {detail !== null && (
              <>
                <dl className="kv">
                  <dt>Type</dt>
                  <dd className="mono">{detail.document.docType}</dd>
                  <dt>Uploaded</dt>
                  <dd>
                    {shortDate(detail.document.createdAt)} by {detail.document.uploadedBy}
                  </dd>
                  <dt>Content hash</dt>
                  <dd className="mono">{detail.document.contentSha256.slice(0, 16)}</dd>
                  <dt>Read</dt>
                  <dd>
                    {(() => {
                      const r = readingStatus(detail.reading?.state, detail.reading?.note)
                      return (
                        <>
                          <Status tone={r.tone}>{r.word}</Status>
                          {detail.reading?.readAt ? ' ' + shortDate(detail.reading.readAt) : ''}
                          {r.detail !== '' && <span className="cell-sub">{r.detail}</span>}
                          {readByWords(detail.reading?.readBy) !== '' && (
                            <span className="cell-sub">{readByWords(detail.reading?.readBy)}</span>
                          )}
                        </>
                      )
                    })()}
                  </dd>
                  <dt>Text kept</dt>
                  <dd>
                    {detail.textRetainedBytes ? detail.textRetainedBytes + ' bytes, masked' : 'none'}
                    {detail.reading?.holdsPersonalDataValues && (
                      <span className="cell-sub">Personal data values in it were masked before the text was kept</span>
                    )}
                  </dd>
                </dl>

                {(detail.reading?.restrictedToEvidenceReaders || detail.reading?.legacyTextHoldsValues) && (
                  <p className="note sw-row">
                    <Status tone="info">Original restricted</Status>
                    <span>
                      {detail.reading?.legacyTextHoldsValues
                        ? 'An upload record from before release 3.3 keeps this file\'s text unmasked, and it holds personal data. It is flagged, never changed.'
                        : 'The original file may hold personal data values, so it opens only for people who may read evidence.'}
                    </span>
                    <Toggletip label="Why the original is restricted">
                      The text everybody reads is masked, same length, so every page and line reference still points at
                      the right place. The file itself is kept as the evidence it is, and opened only by evidence readers.
                    </Toggletip>
                  </p>
                )}

                {rereadError != null && <ErrorBox error={rereadError} what="The document" verb="queued to be read" />}
                {(detail.reading?.state === 'READ' || detail.reading?.state === 'NOT_READ') && can('evidence.review') && (
                  <div className="chiprow">
                    <button
                      type="button"
                      disabled={rereading}
                      onClick={() => readAgain(detail.document.id)}
                    >
                      {rereading ? 'Asking' : 'Read the file again'}
                    </button>
                  </div>
                )}

                {!detail.extractable && (
                  <p className="note stop sw-row">
                    <Status tone={stillReading(detail.reading?.state) ? 'info' : 'crit'}>
                      {stillReading(detail.reading?.state) ? 'Being read' : 'Not readable'}
                    </Status>
                    <span>
                      {stillReading(detail.reading?.state)
                        ? 'Its clauses can be read as soon as the file has been read'
                        : 'No text was kept, so no clause can be read'}
                    </span>
                    <Toggletip label="What an unreadable file still proves">
                      It is on file and hashed, so it stands as evidence that the agreement exists. Treating it as a
                      contract that says nothing would be wrong: it was never read.
                    </Toggletip>
                  </p>
                )}

                {detail.extractable && (
                  <>
                    {published.length === 0 ? (
                      <p className="note warn sw-row">
                        <Status tone="warn">None published</Status>
                        <span>Publish a questionnaire in the studio first</span>
                        <Toggletip label="Why a questionnaire is needed">
                          A contract is read against a published questionnaire version.
                        </Toggletip>
                      </p>
                    ) : (
                      <div className="field">
                        <label htmlFor="vc-qcode">Read it against</label>
                        <select
                          id="vc-qcode"
                          value={qCode}
                          onChange={(e) => setQCode(e.target.value)}
                          aria-describedby="vc-qcode-note"
                        >
                          {published.map((q) => (
                            <option key={q.id} value={q.code}>
                              {q.name} (v{q.publishedVersionNo}, {q.publishedQuestionCount} questions)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {extractError != null && <ErrorBox error={extractError} what="The clauses" verb="read" />}

                    <div className="chiprow">
                      <button
                        type="button"
                        className="primary"
                        onClick={extract}
                        disabled={extracting || qCode === '' || published.length === 0 || !can('evidence.review')}
                        title={can('evidence.review') ? undefined : refusalReason('evidence.review')}
                        data-action="tprm.vendor,vendor-record.a13"
                      >
                        {extracting ? 'Reading' : detail.extraction ? 'Read it again' : 'Read the clauses'}
                      </button>
                      {detail.extraction && (
                        <span className="muted">
                          Last read {shortDate(detail.extraction.extractedAt)} by{' '}
                          {detail.extraction.extractedBy}, rule table{' '}
                          <span className="mono">{detail.extraction.ruleTableSha256.slice(0, 12)}</span>
                        </span>
                      )}
                    </div>

                  </>
                )}
              </>
            )}
          </div>

          {coverage !== null && (
            <div className={coverage.unanswered.length > 0 ? 'note warn' : 'note'} role="status">
              <strong>
                {coverage.unanswered.length > 0
                  ? 'This run left ' +
                    plural(coverage.unanswered.length, 'question', 'questions') +
                    ' with no proposal'
                  : 'Every question in this version drew a proposal'}
              </strong>
              {coverage.unanswered.length > 0 ? (
                <>
                  {' '}
                  No rule matched <span className="mono">{coverage.unanswered.join(', ')}</span>{' '}
                  <Toggletip label="What an unanswered question means">
                    Those questions stay unanswered, and how many there are decides whether this vendor still has to be
                    sent a questionnaire.
                  </Toggletip>
                </>
              ) : (
                <>
                  {' '}
                  Each one still has to be read and accepted below{' '}
                  <Toggletip label="Coverage is not agreement">
                    That is coverage of the question set, not agreement with it.
                  </Toggletip>
                </>
              )}
              {coverage.unusable.length > 0 && (
                <>
                  <ul className="issue-list">
                    {coverage.unusable.map((u) => (
                      <li key={u.questionKey}>
                        <span className="mono">{u.questionKey}</span> {u.reason}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {set !== null && proposals.length === 0 && (
            <div className="empty">
              <strong>It was read and no clause matched</strong>
              Every question stays unanswered, so send the questionnaire{' '}
              <Toggletip label="What no match means">
                That is a finding about this contract, not a fault in it.
              </Toggletip>
            </div>
          )}

          {set !== null && proposals.length > 0 && (
            <>
              <div className="stat-strip">
                <div className="stat">
                  <b>{proposals.length}</b>
                  <span>proposed</span>
                </div>
                <div className="stat">
                  <b>{acceptedOnDoc}</b>
                  <span>accepted</span>
                </div>
                <div className="stat">
                  <b>{rejectedOnDoc}</b>
                  <span>rejected</span>
                </div>
                <div className={undecided.length > 0 ? 'stat alert' : 'stat'}>
                  <b>{undecided.length}</b>
                  <span>waiting on you</span>
                </div>
              </div>

              <p className="note warn sw-row">
                <strong>Confidence runs 0 to 1 and is not accuracy</strong>
                <Toggletip label="What confidence means">
                  The rule table's estimate of how cleanly a phrase matched, against no threshold. A low number marks a
                  proposal that needs reading, not one that is wrong.
                </Toggletip>
              </p>

              <ul className="exc-list">
                {proposals.map((p) => {
                  const d = drafts[p.questionKey]
                  const already = decidedStatus(p.status)
                  const issue = serverIssues[p.questionKey]
                  const base = 'vc-' + p.questionKey
                  const conflicts = conflictsOf(p)
                  return (
                    <li className="exc" key={p.questionKey}>
                      <div className="exc-head">
                        <h4 className="exc-title">{p.control}</h4>
                        {already === 'accepted' ? (
                          <Chip s={statusWord('document', 'VALID')} />
                        ) : already === 'rejected' ? (
                          <Status tone="crit">Rejected</Status>
                        ) : (
                          <Status tone="info">Proposed</Status>
                        )}
                      </div>

                      <div className="exc-meta">
                        <span className="mono">{p.questionKey}</span>
                        <span>
                          reads as <span className="owner">{p.proposedValue}</span>
                        </span>
                        <span>
                          confidence <span className="mono">{p.confidence.toFixed(2)}</span> of 1,{' '}
                          {confidenceReading(p.confidence)}
                        </span>
                      </div>

                      {/* The default blockquote indent would set the clause apart from
                          the reasoning that has to be read against it. */}
                      <blockquote className="note" style={{ marginInline: 0 }}>
                        <strong>{p.locator}</strong>
                        {p.snippet}
                      </blockquote>

                      <p className="cites">
                        <span className="cite">matched on: {p.matchedPhrase}</span>
                      </p>

                      {/* A warning that asks for action stays on the screen,
                          as one line. What to check is behind the (i). */}
                      <p className="note warn vd-inline">
                        <strong>Read this before you agree</strong>
                        <ReasonTip hover label="What to check before you agree" text={p.why} />
                      </p>

                      {conflicts.length > 0 && (
                        <div className="note stop">
                          <strong>Another clause of this contract says something else</strong>{' '}
                          <Toggletip label="Why a second reading matters">
                            The reading above is the stronger match, not the agreed one. A contract that says two
                            things about one control is a finding in its own right.
                          </Toggletip>
                          <ul className="issue-list">
                            {conflicts.map((c) => (
                              <li key={c.locator + c.proposedValue}>
                                <span className="mono">{c.locator}</span> reads as <b>{c.proposedValue}</b>
                                {c.snippet !== '' && <span className="cell-sub">{c.snippet}</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="exc-actions" role="group" aria-label={'Decision on ' + p.control}>
                        <button
                          type="button"
                          className="chip-toggle"
                          aria-pressed={d?.verdict === 'ACCEPT'}
                          onClick={() => setVerdict(p.questionKey, 'ACCEPT')}
                        >
                          Accept the reading
                        </button>
                        <button
                          type="button"
                          className="chip-toggle"
                          aria-pressed={d?.verdict === 'EDIT'}
                          onClick={() => setVerdict(p.questionKey, 'EDIT')}
                        >
                          Accept the clause, correct the value
                        </button>
                        <button
                          type="button"
                          className="chip-toggle"
                          aria-pressed={d?.verdict === 'REJECT'}
                          onClick={() => setVerdict(p.questionKey, 'REJECT')}
                        >
                          Reject
                        </button>
                      </div>

                      {d?.verdict === 'EDIT' && (
                        <div className="reject-form">
                          <div className="field">
                            <label htmlFor={base + '-value'}>Value to record instead</label>
                            <input
                              id={base + '-value'}
                              type="text"
                              value={d.value}
                              onChange={(e) => patch(p.questionKey, 'value', e.target.value)}
                              aria-describedby={base + '-value-note ' + base + '-edit-error'}
                            />
                            <p className="field-note" id={base + '-value-note'}>
                              Proposed <span className="mono">{p.proposedValue}</span>, use an answer this question
                              takes
                            </p>
                          </div>
                          <div className="field">
                            <label htmlFor={base + '-note'}>Why you are changing it</label>
                            <textarea
                              id={base + '-note'}
                              rows={2}
                              value={d.note}
                              onChange={(e) => patch(p.questionKey, 'note', e.target.value)}
                              aria-describedby={base + '-note-help ' + base + '-edit-error'}
                            />
                          </div>
                          <p className="field-error" id={base + '-edit-error'} role="alert">
                            {d.value.trim() === '' || d.note.trim() === ''
                              ? 'A corrected value and a reason are both needed'
                              : ''}
                          </p>
                        </div>
                      )}

                      {d?.verdict === 'REJECT' && (
                        <div className="reject-form">
                          <div className="field">
                            <label htmlFor={base + '-reject'}>Why you are rejecting it</label>
                            <input
                              id={base + '-reject'}
                              type="text"
                              value={d.note}
                              onChange={(e) => patch(p.questionKey, 'note', e.target.value)}
                              aria-describedby={base + '-reject-help'}
                            />
                          </div>
                        </div>
                      )}

                      {issue && (
                        <p className="field-error" role="alert">
                          {issue.code}: {issue.detail}
                        </p>
                      )}
                    </li>
                  )
                })}
              </ul>

              {submitError != null && <ErrorBox error={submitError} what="The decisions" verb="recorded" />}

              <div className="commit-bar">
                <button
                  type="button"
                  className="primary"
                  onClick={submit}
                  disabled={
                    submitting ||
                    decided.length === 0 ||
                    incomplete.length > 0 ||
                    (undecided.length > 0 && !partial) ||
                    !can('evidence.review')
                  }
                  title={can('evidence.review') ? undefined : refusalReason('evidence.review')}
                  data-action="tprm.vendor,vendor-record.a14"
                >
                  {submitting ? 'Recording' : 'Record ' + decided.length + ' of ' + proposals.length}
                </button>

                {undecided.length > 0 && (
                  <label className="chip-toggle">
                    <input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} />{' '}
                    Record a partial set and leave {undecided.length} undecided
                  </label>
                )}

                {submitNote !== '' && (
                  <span className="ok-note" role="status">
                    {submitNote}
                  </span>
                )}
              </div>
            </>
          )}

          {set === null && detail !== null && detail.extractable && detailError == null && (
            <div className="empty">
              <strong>Nothing read out of this document yet</strong>
              Pick a questionnaire above and read the clauses{' '}
              <Toggletip label="What reading changes">
                Reading writes to no assessment and changes no vendor record.
              </Toggletip>
            </div>
          )}
        </div>
      )}

      <div className="section-head">Build the provisional assessment</div>

      <div>
        <p className="note stop sw-row">
          <Status tone="crit">Not attested</Status>
          <strong>Creates an assessment nobody at the vendor has attested to</strong>
          <button type="button" className="linkish" onClick={() => setAboutBuild(true)}>
            About this build
          </button>
        </p>
        <Dialog shape="drawer" kind="record" open={aboutBuild} onClose={() => setAboutBuild(false)} title="About the provisional assessment">
          <div className="sw-stack">
            <p>
              Every proposal accepted across this vendor's documents becomes an answer carrying DOCUMENT_EXTRACTED
              provenance, or INTERNAL_ASSERTED where you corrected the value.
            </p>
            <p>
              The assessment opens as PROVISIONAL with a date by which the vendor has to confirm it, and if they do
              not confirm by then the vendor counts as unassessed.
            </p>
            <p>A vendor may hold one open assessment only, so this is refused while another is already running.</p>
            <p>Only accepted proposals go into the build, one answer per question, so the same clause accepted on two documents counts once.</p>
          </div>
        </Dialog>

        {refusal !== null ? (
          <p className="note warn" id="vc-build-gate" role="alert">
            {refusal.detail.endsWith('.') ? refusal.detail : refusal.detail + '.'}
            {refusal.code === 'NOTHING_ACCEPTED'
              ? ' Accept at least one clause above, then try again.'
              : ' Add the contact on the Contacts tab, then try again.'}
          </p>
        ) : nothingAccepted ? (
          <p className="note warn" id="vc-build-gate">
            Nothing accepted yet, {onFile.length === 0 ? 'put a contract on file above' : 'open a document above'} and
            accept a clause
          </p>
        ) : docs !== null ? (
          <p className="muted" id="vc-build-gate">
            <strong>{acceptedAcross}</strong> accepted{' '}
            {docsWithAccepted === 1 ? 'on one document' : 'across ' + docsWithAccepted + ' documents'}
            {docsWithAccepted > 1 && openId !== null && set !== null ? ', ' + acceptedOnDoc + ' on the open one' : ''}
          </p>
        ) : null}

        {buildError != null && refusal === null && (
          <ErrorBox error={buildError} what="The provisional assessment" verb="created" />
        )}

        <div className="form-grid">
          <div className="field">
            <label htmlFor="vc-build-q">Questionnaire</label>
            <select
              id="vc-build-q"
              value={buildQ}
              onChange={(e) => setBuildQ(e.target.value)}
              aria-describedby="vc-build-q-note"
            >
              {published.map((q) => (
                <option key={q.id} value={q.code}>
                  {q.name} (v{q.publishedVersionNo})
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="vc-build-model">Risk model</label>
            <select
              id="vc-build-model"
              value={buildModel}
              onChange={(e) => setBuildModel(e.target.value)}
              aria-describedby="vc-build-model-note"
            >
              <option value="">None</option>
              {models.map((m) => (
                <option key={m.id} value={m.code}>
                  {m.name} (v{m.activeVersionNo})
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="vc-build-days">Days the vendor has to confirm</label>
            <input
              id="vc-build-days"
              type="number"
              min={1}
              max={365}
              value={buildDays}
              onChange={(e) => setBuildDays(e.target.value)}
              aria-describedby="vc-build-days-note"
            />
          </div>

          {(buildReason !== '' || (buildError instanceof ApiError && buildError.code === 'SUSPENDED_NEEDS_REASON')) && (
            <div className="field wide">
              <label htmlFor="vc-build-reason">Why a suspended vendor is sent this</label>
              <textarea
                id="vc-build-reason"
                rows={2}
                maxLength={1000}
                value={buildReason}
                onChange={(e) => setBuildReason(e.target.value)}
                aria-describedby="vc-build-reason-note"
              />
              <p className="muted" id="vc-build-reason-note">
                Kept on the vendor's record with the assessment
              </p>
            </div>
          )}
        </div>

        <div className="chiprow">
          <button
            type="button"
            className="primary"
            onClick={build}
            disabled={building || buildQ === '' || published.length === 0 || nothingAccepted || !can('evidence.review')}
            title={can('evidence.review') ? undefined : refusalReason('evidence.review')}
            aria-describedby={docs !== null || refusal !== null ? 'vc-build-gate' : undefined}
            data-action="tprm.vendor,vendor-record.a15"
          >
            {building ? 'Building' : 'Create the provisional assessment'}
          </button>
          <span className="muted">The vendor would have {dueDays} days to confirm it</span>
        </div>

        {built && (
          <p className="ok-note sw-row" role="status">
            <span>
              Created <span className="mono">{built.reference}</span>, provisional
              {built.due !== '' && (
                <>
                  , the vendor confirms by {shortDate(built.due)}
                  {daysUntil(built.due) !== null && <> ({daysUntil(built.due)} days)</>}
                </>
              )}
            </span>
            <Toggletip label="What provisional means">
              Until the vendor confirms it, none of this closes a regulatory obligation on its own.
            </Toggletip>
          </p>
        )}
      </div>
    </section>
  )
}
